import { useTranslation } from 'react-i18next';
import { toHumanReadable } from '../core/utils/string.ts';
import { GROUP_SEPARATOR } from '../document/pages/Assistance/useDocumentConfig.ts';

export { GROUP_SEPARATOR };

export const LINE_ITEM_PREFIX = 'line_items';

const resolveLabel = (t: (key: string) => string, section: string, stripped: string): string => {
    const key = `${section}.fieldNames.${stripped}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
    const leaf = stripped.split(GROUP_SEPARATOR).slice(-1)[0];
    return toHumanReadable(leaf);
};

const stripLineItemPrefix = (path: string): { stripped: string; isLineItem: boolean } => {
    const isLineItem = path.startsWith(`${LINE_ITEM_PREFIX}${GROUP_SEPARATOR}`);
    const stripped = isLineItem ? path.slice(LINE_ITEM_PREFIX.length + GROUP_SEPARATOR.length) : path;
    return { stripped, isLineItem };
};

const useFieldLabel = (section?: 'headerView' | 'itemsView') => {
    const { t } = useTranslation('assistance');
    return (path: string) => {
        const { stripped, isLineItem } = stripLineItemPrefix(path);
        const resolvedSection = section ?? (isLineItem ? 'itemsView' : 'headerView');
        return resolveLabel(t, resolvedSection, stripped);
    };
};

export const useGroupedFieldLabel = (section?: 'headerView' | 'itemsView') => {
    const resolve = useFieldLabel(section);
    return (path: string) => {
        const { stripped } = stripLineItemPrefix(path);
        const parts = stripped.split(GROUP_SEPARATOR);
        if (parts.length <= 1) return resolve(path);
        const prefix = path.slice(0, path.length - stripped.length);
        const groupLabel = resolve(`${prefix}${parts[0]}`);
        const fieldLabel = resolve(path);
        return `${groupLabel}: ${fieldLabel}`;
    };
};

export default useFieldLabel;
